/*
 * @Author: alex
 * @Date: 2022-08-27 15:34:19
 * @LastEditors: Alex yxfacw@163.com
 * @LastEditTime: 2023-07-17 18:00:32
 * @Description:
 * Copyright (c) 2022 by piesat, All Rights Reserved.
 */
import { Point } from 'ol/geom'
import { LifecycleState } from '@vmap/core'
import { BaseMapTool } from './mapToolBase'
import { Fill, Stroke, Style, Circle } from 'ol/style'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import type Layer from 'ol/layer/Layer'
import { GeoJSON } from 'ol/format'
import { transformExtent } from 'ol/proj'
import { createEmpty, extend, buffer } from 'ol/extent'
import { getNanoId, getByKey } from '@vmap/core'

import bbox from '@turf/bbox'
import { Feature } from 'ol'

import type { FeatureLike } from 'ol/Feature'
import type { StyleLike } from 'ol/style/Style'
import { mapContext } from '../mapContext'
import { error, info } from '../console'
// import { EPSG4326 } from '../projection/mapProjection'
interface MapRenderEvent {
  [_: string]: any
}

const defaultStyle: Style = new Style({
  fill: new Fill({
    color: [30, 144, 255, 0.4]
  }),
  stroke: new Stroke({
    color: 'rgba(75,0,130, 1)',
    width: 4
  }),
  image: new Circle({
    radius: 6,
    fill: new Fill({
      color: [0, 0, 255, 1]
    }),
    stroke: new Stroke({
      color: [255, 255, 255, 1],
      width: 3 / 2
    })
  })
})

export default class MapRenderTool extends BaseMapTool<MapRenderEvent> {
  /**
   * 存储图层的引用 用于缓存图层
   */
  private readonly layers: Map<
    string,
    { bbox: number[] | undefined; layer: Layer }
  > = new Map()

  private readonly highlightLayer = new VectorLayer({})

  protected startInternal() {
    // 准备状态
    if (this.map === undefined) {
      error('地图对象未设置')
      this.state = LifecycleState.ERROR
    } else {
      // 添加高亮图层
      this.highlightLayer.set('id', 'highlight-vector-layer')
      this.map.addLayer(this.highlightLayer)
    }
  }

  protected stopInternal() {
    // 移除高亮图层
    this.highlightLayer.setSource(null)
    this.map.removeLayer(this.highlightLayer)
  }

  clear() {
    //
    this.layers.clear()
  }

  /**
   * FIXME: 高亮features
   * demo:
   *
   * const fs = new GeoJSON({
   *     dataProjection: 'EPSG:4527',
   *     featureProjection: 'EPSG:3857'
   *   }).readFeatures(JSON.stringify(data))
   *   console.log(fs)
   *   // 高亮
   *   renderTool.highlightFeature(fs, true, 'EPSG:3857', style, 10)
   *
   * @param features 要高亮的图形，注意geojson的坐标系需要和地图保持一致
   * @param clearFirst 是否先清空图层
   * @param extentBufferSize 范围 buffer 数值
   * @param style 样式
   * @param clearAfterSeconds 多少秒后自动清除
   */
  highlightFeatures(
    features: Feature[],
    clearFirst = true,
    extentBufferSize = 1,
    style?: Style | undefined,
    clearAfterSeconds?: number
  ) {
    if (features.length === 0) {
      error('features cannot be empty')
      return
    }
    if (clearFirst) {
      this.highlightLayer.setSource(null)
      this.highlightLayer.setVisible(false)
    }
    this.highlightLayer.setSource(
      new VectorSource({
        features: features,
        format: new GeoJSON()
      })
    )
    this.highlightLayer.setStyle(style !== undefined ? style : defaultStyle)
    this.highlightLayer.setVisible(true)
    // 这里需要获取地图已加载图层的最大 zindex
    let maxZ = this.highlightLayer.getZIndex()
    console.log('zindex: ', maxZ)
    this.map.getAllLayers().forEach(lyr => {
      if (lyr.getZIndex() > maxZ || maxZ === undefined) {
        maxZ = lyr.getZIndex()
      }
    })
    // if (maxZ >= this.highlightLayer.getZIndex()) {
    //   this.highlightLayer.setZIndex(Infinity)
    // }
    this.highlightLayer.setZIndex(Infinity)
    console.log(maxZ)
    // 缩放至范围内
    const extent = createEmpty()
    features.forEach(feat => {
      if (feat.getGeometry()?.getExtent()) {
        extend(extent, feat.getGeometry()?.getExtent() ?? createEmpty())
      }
    })
    if (extent) {
      this.map.getView().fit(buffer(extent, extentBufferSize), {
        nearest: true
      })
    }
    if (clearAfterSeconds !== undefined && clearAfterSeconds > 0) {
      setTimeout(() => {
        features.forEach(feat =>
          this.highlightLayer.getSource()?.removeFeature(feat)
        )
      }, clearAfterSeconds * 1000)
    }
  }

  /**
   * 清除高亮图层
   */
  clearHightLightLayer() {
    this.highlightLayer.setSource(null)
    this.highlightLayer.setVisible(false)
  }

  /**
   * 从数组里渲染点图层
   * @param points 点数据
   * @param xFieldName x字段名
   * @param yFieldName y字段名
   * @param layerId 图层id
   * @param style 图层样式
   */
  renderPoints<T extends { [_: string]: any }>(
    points: T[],
    xFieldName: string,
    yFieldName: string,
    layerId: string = getNanoId(),
    style?: Style | StyleLike
  ): string | unknown {
    if (points.length === 0) {
      return
    }
    // 先从layers 中获取 如果存在 直接使用
    if (
      this.layers.has(layerId) &&
      this.layers.get(layerId)?.layer !== undefined
    ) {
      const vlyr = this.layers.get(layerId)?.layer ?? new VectorLayer()
      const existedIdx = this.map
        .getAllLayers()
        .findIndex(lyr => lyr.get('id') === layerId)
      if (existedIdx < 0) {
        this.map.addLayer(vlyr)
        mapContext.fire('map_layer_added', { lyrId: layerId })
      }
      if (this.layers.get(layerId)?.bbox !== undefined) {
        this.map.getView().fit(this.layers.get(layerId)?.bbox ?? [], {
          nearest: true
        })
      }
    } else {
      // 将数组组织成 feature 数组
      const features = points.map(pnt => {
        const x = getByKey(pnt, xFieldName) as number
        const y = getByKey(pnt, yFieldName) as number
        return new Feature({ geometry: new Point([x, y]), properties: pnt })
      })
      const vl = new VectorLayer({
        source: new VectorSource({
          features: features,
          format: new GeoJSON()
        }),
        style: style ?? defaultStyle
      })
      vl.set('id', layerId)
      this.map.addLayer(vl)
      mapContext.fire('map_layer_added', { lyrId: layerId })
      this.layers.set(vl.get('id') as string, { layer: vl, bbox: undefined })
    }
    return layerId
  }

  /**
   * 渲染 geojson 数据
   * @param url 获取 geojson 的url
   * @param style  渲染 geojson 样式
   * @param layerId 指定 layerid
   * @param clearAfterSeconds  在xx秒后自动清除
   */
  renderGeoJSON(
    url: string,
    style?: Style,
    layerId: string = getNanoId(),
    clearAfterSeconds?: number
  ) {
    // 先从layers 中获取 如果存在 直接使用
    if (
      this.layers.has(layerId) &&
      this.layers.get(layerId)?.layer !== undefined
    ) {
      const vlyr = this.layers.get(layerId)?.layer ?? new VectorLayer()
      this.map.addLayer(vlyr)
      this.map.getView().fit(this.layers.get(layerId)?.bbox ?? [], {
        nearest: true
      })
      if (clearAfterSeconds !== undefined && clearAfterSeconds > 0) {
        setTimeout(() => {
          this.map.removeLayer(vlyr)
        }, clearAfterSeconds * 1000)
      }
    } else {
      const vl = new VectorLayer({
        source: new VectorSource({ url: url, format: new GeoJSON() }),
        //   map: this.map,
        style: style ?? defaultStyle,
        zIndex: Infinity
      })
      vl.set('id', layerId)
      this.map.addLayer(vl)
      info('lyr:', vl)
      // 缩放至图层范围内
      fetch(url)
        .then(res => res.json())
        .then(res => {
          const bBox = bbox(res)
          const extent = transformExtent(
            bBox,
            'EPSG:4326',
            this.map.getView().getProjection()
          )
          this.map.getView().fit(extent, {
            nearest: true
          })
          this.layers.set(vl.get('id') as string, { layer: vl, bbox: extent })
        })
        .catch(err => {
          console.log(err)
        })
      // TODO: 执行渲染后回调
      if (clearAfterSeconds !== undefined && clearAfterSeconds > 0) {
        setTimeout(() => {
          this.map.removeLayer(vl)
          // this.layers.delete(vl.get('id') as string)
        }, clearAfterSeconds * 1000)
      }
    }
  }

  /**
   * 渲染 feature
   * @param features 图形要素
   * @param style    高亮样式
   * @param layerId  图形id
   * @param clearAfterSeconds  多少秒后清除
   */
  renderFeature(
    feature: FeatureLike,
    style?: Style,
    layerId: string = getNanoId(),
    clearAfterSeconds?: number
  ) {
    // 先从layers 中获取 如果存在 直接使用
    if (
      this.layers.has(layerId) &&
      this.layers.get(layerId)?.layer !== undefined
    ) {
      const vlyr = this.layers.get(layerId)?.layer ?? new VectorLayer()
      this.map.addLayer(vlyr)
      this.map.getView().fit(this.layers.get(layerId)?.bbox ?? [], {
        nearest: true
      })
      if (clearAfterSeconds !== undefined && clearAfterSeconds > 0) {
        setTimeout(() => {
          this.map.removeLayer(vlyr)
        }, clearAfterSeconds * 1000)
      }
    } else {
      const feat = new GeoJSON().readFeature(feature)
      const vl = new VectorLayer({
        source: new VectorSource({
          format: new GeoJSON(),
          features: [feat]
        }),
        //   map: this.map,
        style: style ?? defaultStyle
      })
      vl.set('id', layerId)
      this.map.addLayer(vl)
      // 缩放至图层范围内
      const bBox = bbox(JSON.parse(new GeoJSON().writeFeature(feat)))
      const extent = transformExtent(
        bBox,
        'EPSG:4326',
        this.map.getView().getProjection()
      )
      this.map.getView().fit(extent, {
        nearest: true
      })
      this.layers.set(vl.get('id') as string, { layer: vl, bbox: extent })
      // TODO: 执行渲染后回调

      if (clearAfterSeconds !== undefined && clearAfterSeconds > 0) {
        setTimeout(() => {
          this.map.removeLayer(vl)
          // console.log('删除图层后')
          // this.layers.delete(vl.get('id') as string)
        }, clearAfterSeconds * 1000)
      }
    }
  }
}
